import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, Alert, Spin, Result } from 'antd';
import { LockOutlined, DownloadOutlined, ClockCircleOutlined, MailOutlined, LinkOutlined } from '@ant-design/icons';
import api from '../services/api';
import { createAutoAccessGate, getAccessPayload } from '../utils/shareSecurity';
import './FileAccess.css';

const { Title, Paragraph, Text } = Typography;

const FileAccess = () => {
  const { fileId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [error, setError] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const [requireEmail, setRequireEmail] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(true);
  const autoAccessGate = useRef(null);

  if (!autoAccessGate.current) {
    autoAccessGate.current = createAutoAccessGate();
  }

  useEffect(() => {
    checkFileStatus();
  }, [fileId]);

  const checkFileStatus = async () => {
    try {
      const response = await api.get(`/api/files/status/${fileId}`);
      const nextRequireEmail = response.data.requireEmail || false;
      const nextPasswordRequired = response.data.passwordRequired !== false;
      setIsExpired(false);
      setRequireEmail(nextRequireEmail);
      setPasswordRequired(nextPasswordRequired);

      if (autoAccessGate.current.shouldStart({
        shareId: fileId,
        passwordRequired: nextPasswordRequired,
        requireEmail: nextRequireEmail,
      })) {
        await handleSubmit({}, { passwordRequired: nextPasswordRequired });
      }
    } catch (error) {
      if (error.response?.status === 410) {
        setIsExpired(true);
        setError(error.response?.data?.error || 'This file has expired and been deleted.');
      } else if (error.response?.status === 404) {
        setError('File not found.');
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubmit = async (values, options = {}) => {
    setLoading(true);
    setError('');
    setFileInfo(null);

    try {
      const response = await api.post(
        `/api/files/access/${fileId}`,
        getAccessPayload({
          passwordRequired: options.passwordRequired ?? passwordRequired,
          values,
        }),
      );

      setFileInfo(response.data);
      
      // Auto-download the file
      const link = document.createElement('a');
      link.href = response.data.downloadUrl;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('File not found. It may have been deleted.');
      } else if (error.response?.status === 410) {
        setError(error.response?.data?.error || 'This file has expired and been deleted.');
      } else if (error.response?.status === 400) {
        setError(error.response?.data?.error || 'Invalid request. Please check your input.');
      } else if (error.response?.status === 401) {
        setError('Invalid password. Please try again.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (checkingStatus) {
    return (
      <div className="file-access-page">
        <Card className="file-access-card">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: 20 }}>Checking file status...</Paragraph>
          </div>
        </Card>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="file-access-page">
        <Card className="file-access-card">
          <Result
            status="error"
            icon={<ClockCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="File Has Expired"
            subTitle="This file has expired and been deleted from our servers."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="file-access-page">
      <Card className="file-access-card">
        <div className="file-access-header">
          {passwordRequired
            ? <LockOutlined className="access-icon" />
            : <LinkOutlined className="access-icon" />}
          <Title level={2}>
            {passwordRequired ? 'Password Protected File' : 'Private File Link'}
          </Title>
          <Paragraph>
            {passwordRequired && requireEmail && 'Enter your email and the password to access this file'}
            {passwordRequired && !requireEmail && 'Enter the password to access this file'}
            {!passwordRequired && requireEmail && 'Enter your email to download this file'}
            {!passwordRequired && !requireEmail && 'Your download is being prepared from this private link'}
          </Paragraph>
        </div>

        {error && !isExpired && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            className="access-error"
          />
        )}

        {fileInfo && (
          <Alert
            message="File Access Granted"
            description={
              <div>
                <Paragraph>Your download should start automatically.</Paragraph>
                <div className="file-info">
                  <Text strong>File: </Text>
                  <Text>{fileInfo.filename}</Text>
                  <br />
                  <Text strong>Size: </Text>
                  <Text>{formatFileSize(fileInfo.size)}</Text>
                </div>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  href={fileInfo.downloadUrl}
                  download={fileInfo.filename}
                >
                  Download again
                </Button>
              </div>
            }
            type="success"
            showIcon
            className="access-success"
          />
        )}

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          disabled={loading || fileInfo}
        >
          {requireEmail && (
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Email is required to download this file' },
                { type: 'email', message: 'Please enter a valid email address' },
              ]}
            >
              <Input
                size="large"
                placeholder="Enter your email address"
                prefix={<MailOutlined />}
              />
            </Form.Item>
          )}

          {passwordRequired && (
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter the password' }]}
            >
              <Input.Password
                size="large"
                placeholder="Enter file password"
                prefix={<LockOutlined />}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              block
            >
              {loading
                ? (passwordRequired ? 'Verifying...' : 'Preparing download...')
                : (passwordRequired ? 'Access File' : 'Download File')}
            </Button>
          </Form.Item>
        </Form>

        <div className="file-access-footer">
          <Paragraph type="secondary">
            {passwordRequired
              ? 'This file is password protected. You need the correct password from the file owner to access it.'
              : 'This file uses a private, unguessable link instead of a password. Do not forward it beyond the intended recipients.'}
          </Paragraph>
        </div>
      </Card>
    </div>
  );
};

export default FileAccess;
